import * as React from 'react';
import { Table, Typography, Box } from '@mui/joy';
import { TableSkeleton } from './LoadingSkeleton';

const StyledTable = React.forwardRef(({ columns = [], rows = [], loading = false, title = null }, ref) => {
  const [sortConfig, setSortConfig] = React.useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedRows = React.useMemo(() => {
    if (!sortConfig.key) return rows;
    return [...rows].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sortConfig]);

  return (
    <Box ref={ref}>
      {title && <Typography level="h2" color="success" sx={{ mb: 2 }}>{title}</Typography>}
      {loading ? (
        <TableSkeleton rows={5} columns={columns.length} />
      ) : (
        <Table
          size="sm"
          stickyHeader
          borderAxis="bothBetween"
          hoverRow
          sx={{ 
            minWidth: 700,
            '& thead th': {
              bgcolor: 'success.50',
              color: 'success.800',
              fontWeight: 600,
              borderBottom: '2px solid',
              borderColor: 'success.200',
              fontSize: '0.75rem',
              textTransform: 'none',
              letterSpacing: '0.25px',
              padding: '8px 12px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              '&:hover': {
                bgcolor: 'success.100',
              },
            },
            '& tbody tr': {
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'success.25',
                transform: 'scale(1.005)',
                boxShadow: '0 2px 8px rgba(0, 168, 132, 0.1)',
              },
              '&:nth-of-type(even)': {
                bgcolor: 'rgba(248, 250, 252, 0.5)',
              },
              '&:nth-of-type(even):hover': {
                bgcolor: 'success.25',
              },
            },
            '& tbody td': {
              padding: '8px 12px',
              borderColor: 'neutral.200',
              fontSize: '0.8125rem',
            },
            borderRadius: 'md',
            overflow: 'hidden',
            boxShadow: 'sm',
          }}
        >
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  style={{ cursor: 'pointer' }}
                >
                  {col.label}
                  {sortConfig.key === col.key
                    ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼')
                    : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length > 0 ? (
              sortedRows.map((row, idx) => (
                <tr key={row.id || idx} style={row._rowSx || {}}>
                  {columns.map((col) => (
                    <td key={col.key + '-' + row.id}>{row[col.key]}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length}>
                  <Typography textAlign="center">No records found.</Typography>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </Box>
  );
});

export default StyledTable;